import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface UploadQueueItem {
  fileKey: string;
  fileName: string;
  fileSize: number;
  progress: number;
  status: 'pending' | 'uploading' | 'paused' | 'completed' | 'failed';
  uploadUrl?: string;
  filePath?: string;
  bytesUploaded: number;
  totalBytes: number;
  error?: string;
  folderId?: number;
  titleEn?: string;
  titleAr?: string;
  eventId?: number;
  eventDate?: string;
  tagIds?: number[];
  newTags?: string[];
  userId?: number;
  fileCreationDate?: string;
  // File metadata (for reconstruction)
  fileMetadata?: {
    name: string;
    size: number;
    type: string;
    lastModified: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class GlobalUploadQueueService {
  private readonly STORAGE_KEY = 'globalUploadQueue';
  private uploadsMap = new Map<string, UploadQueueItem>();
  private uploads$ = new BehaviorSubject<Map<string, UploadQueueItem>>(new Map());

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Observable stream of all uploads
   */
  get activeUploads$(): Observable<UploadQueueItem[]> {
    return this.uploads$.pipe(
      map(uploadsMap => Array.from(uploadsMap.values()))
    );
  }

  /**
   * Observable stream of active (uploading/paused) uploads only
   */
  get activeUploadsOnly$(): Observable<UploadQueueItem[]> {
    return this.activeUploads$.pipe(
      map(uploads => uploads.filter(u => 
        u.status === 'uploading' || u.status === 'paused' || u.status === 'pending'
      ))
    );
  }

  /**
   * Get count of active uploads
   */
  getUploadCount(): number {
    return Array.from(this.uploadsMap.values()).filter(u => 
      u.status === 'uploading' || u.status === 'paused' || u.status === 'pending'
    ).length;
  }

  /** True while files are pending or actively uploading (excludes paused). */
  hasInProgressUploads(): boolean {
    return Array.from(this.uploadsMap.values()).some(
      u => u.status === 'pending' || u.status === 'uploading'
    );
  }

  /**
   * Get all active uploads
   */
  getActiveUploads(): UploadQueueItem[] {
    return Array.from(this.uploadsMap.values()).filter(u => 
      u.status === 'uploading' || u.status === 'paused' || u.status === 'pending'
    );
  }

  /**
   * Get upload by file key
   */
  getUpload(fileKey: string): UploadQueueItem | undefined {
    return this.uploadsMap.get(fileKey);
  }

  /**
   * Add or update upload in queue
   */
  addUpload(upload: UploadQueueItem): void {
    this.uploadsMap.set(upload.fileKey, upload);
    this.emitUpdate();
    this.saveToStorage();
  }

  /**
   * Update upload progress
   */
  updateUploadProgress(fileKey: string, bytesUploaded: number, progress: number): void {
    const upload = this.uploadsMap.get(fileKey);
    if (upload) {
      upload.bytesUploaded = bytesUploaded;
      upload.progress = progress;
      this.emitUpdate();
      this.saveToStorage();
    }
  }

  /**
   * Update upload status
   */
  updateUploadStatus(fileKey: string, status: UploadQueueItem['status'], error?: string): void {
    const upload = this.uploadsMap.get(fileKey);
    if (upload) {
      upload.status = status;
      if (error) {
        upload.error = error;
      } else if (status === 'completed' || status === 'failed') {
        upload.error = undefined;
      }
      this.emitUpdate();
      this.saveToStorage();
    }
  }

  /**
   * Pause upload - this will trigger pause in the actual upload component
   */
  pauseUpload(fileKey: string): void {
    const upload = this.uploadsMap.get(fileKey);
    if (upload && upload.status === 'uploading') {
      this.updateUploadStatus(fileKey, 'paused');
    }
  }

  /**
   * Resume upload
   */
  resumeUpload(fileKey: string): void {
    const upload = this.uploadsMap.get(fileKey);
    if (upload && upload.status === 'paused') {
      this.updateUploadStatus(fileKey, 'uploading');
    }
  }

  /**
   * Cancel upload - immediately remove from queue
   */
  cancelUpload(fileKey: string): void {
    // Immediately remove from queue (don't mark as failed first)
    this.removeUpload(fileKey);
  }

  /**
   * Remove upload from queue
   */
  removeUpload(fileKey: string): void {
    this.uploadsMap.delete(fileKey);
    this.emitUpdate();
    this.saveToStorage();
  }

  /**
   * Clear completed uploads
   */
  clearCompleted(): void {
    const toRemove: string[] = [];
    this.uploadsMap.forEach((upload, key) => {
      if (upload.status === 'completed' || upload.status === 'failed') {
        toRemove.push(key);
      }
    });
    toRemove.forEach(key => this.uploadsMap.delete(key));
    this.emitUpdate();
    this.saveToStorage();
  }

  /**
   * Clear all uploads
   */
  clearAll(): void {
    this.uploadsMap.clear();
    this.emitUpdate();
    this.saveToStorage();
  }

  /**
   * Emit update to subscribers
   */
  private emitUpdate(): void {
    this.uploads$.next(new Map(this.uploadsMap));
  }

  /**
   * Save to localStorage
   */
  private saveToStorage(): void {
    try {
      const uploadsArray = Array.from(this.uploadsMap.values())
        .filter(u => u.status !== 'completed') // Don't persist completed uploads
        .map(u => ({
          ...u,
          // Don't store File object, only metadata
          file: undefined
        }));
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(uploadsArray));
    } catch (error) {
      console.error('Error saving upload queue to storage:', error);
    }
  }

  /**
   * Load from localStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const uploadsArray: UploadQueueItem[] = JSON.parse(stored);
        uploadsArray.forEach(upload => {
          // Only restore non-completed uploads
          if (upload.status !== 'completed' && upload.status !== 'failed') {
            this.uploadsMap.set(upload.fileKey, upload);
          }
        });
        this.emitUpdate();
      }
    } catch (error) {
      console.error('Error loading upload queue from storage:', error);
    }
  }
}

